import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HiOutlineChevronRight, HiOutlineMenu } from "react-icons/hi";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  getLegalCertificateDiscussionDetails,
  LegalCertificateWorkflowStep,
  skipLegalCertificateStep,
} from "api/legalCertificate";

import InactiveStepView from "./InactiveStepView";
import LegalCertificatePreliminaryDiscussionView, {
  LEGAL_CERTIFICATE_PRELIMINARY_FORM_ID,
} from "./LegalCertificatePreliminaryDiscussionView";
import LegalCertificateApplicationDetailsFromClientView, {
  LEGAL_CERTIFICATE_APPLICATION_DETAILS_FORM_ID,
} from "./LegalCertificateApplicationDetailsFromClientView";
import LegalCertificateInvoiceToClientView, {
  LEGAL_CERTIFICATE_INVOICE_TO_CLIENT_FORM_ID,
} from "./LegalCertificateInvoiceToClientView";
import LegalCertificatePaymentFromClientView, {
  LEGAL_CERTIFICATE_PAYMENT_FROM_CLIENT_FORM_ID,
} from "./LegalCertificatePaymentFromClientView";
import LegalCertificateStampPaperDetailsToVendorView, {
  LEGAL_CERTIFICATE_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID,
} from "./LegalCertificateStampPaperDetailsToVendorView";
import LegalCertificateStampPaperFromVendorView, {
  LEGAL_CERTIFICATE_STAMP_PAPER_FROM_VENDOR_FORM_ID,
} from "./LegalCertificateStampPaperFromVendorView";
import LegalCertificatePreparationOfPowerOfAttorneyView, {
  LEGAL_CERTIFICATE_PREPARATION_OF_POA_FORM_ID,
} from "./LegalCertificatePreparationOfPowerOfAttorneyView";
import LegalCertificateFileFormsView, {
  LEGAL_CERTIFICATE_FILE_FORMS_FORM_ID,
} from "./LegalCertificateFileFormsView";
import LegalCertificateInformClientOfFilingView, {
  LEGAL_CERTIFICATE_INFORM_CLIENT_OF_FILING_FORM_ID,
} from "./LegalCertificateInformClientOfFilingView";
import LegalCertificateStatusOfLegalCertificateView, {
  LEGAL_CERTIFICATE_STATUS_OF_LEGAL_CERTIFICATE_FORM_ID,
} from "./LegalCertificateStatusOfLegalCertificateView";
import LegalCertificatePrepareKitView, {
  LEGAL_CERTIFICATE_PREPARE_KIT_FORM_ID,
} from "./LegalCertificatePrepareKitView";
import LegalCertificateHandOverKitToClientView, {
  LEGAL_CERTIFICATE_HANDOVER_FORM_ID,
} from "./LegalCertificateHandOverKitToClientView";
import LegalCertificateNoticeToClientView, {
  LEGAL_CERTIFICATE_NOTICE_TO_CLIENT_FORM_ID,
} from "./LegalCertificateNoticeToClientView";
import LegalCertificateInformClientView, {
  LEGAL_CERTIFICATE_INFORM_CLIENT_FORM_ID,
} from "./LegalCertificateInformClientView";

type Section = "discussion" | "filing" | "status";

const DISCUSSION_ITEMS: { id: string; label: string }[] = [
  {
    id: "preliminary",
    label: "Preliminary Discussion with the Client",
  },
  { id: "application-details", label: "Application details from Client" },
  { id: "invoice", label: "Invoice to Client" },
  {
    id: "payment",
    label: "Payment from Client",
  },
];

const DISCUSSION_ID_TO_SLUG: Record<string, string> = {
  preliminary: "preliminary-discussion-with-the-client",
  "application-details": "application-details-from-client",
  invoice: "invoice-to-client",
  payment: "payment-from-client-for-legal-certificate",
};

const DISCUSSION_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(DISCUSSION_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const FILING_ITEMS: { id: string; label: string }[] = [
  {
    id: "stamp-paper-details-to-vendor",
    label: "Stamp Paper details to Vendor",
  },
  {
    id: "stamp-paper-from-vendor",
    label: "Stamp Paper from Vendor",
  },
  {
    id: "preparation-of-power-of-attorney",
    label: "Preparation of Power of Attorney",
  },
  { id: "file-forms", label: "File forms" },
  {
    id: "inform-client-of-filing",
    label: "Inform Client about Filing",
  },
];

const FILING_ID_TO_SLUG: Record<string, string> = {
  "stamp-paper-details-to-vendor": "stamp-paper-details-to-vendor",
  "stamp-paper-from-vendor": "stamp-paper-from-vendor",
  "preparation-of-power-of-attorney": "preparation-of-power-of-attorney",
  "file-forms": "file-forms",
  "inform-client-of-filing": "inform-client-of-filing",
};

const FILING_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(FILING_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const STATUS_ITEMS: { id: string; label: string }[] = [
  { id: "received", label: "Status of Legal Certificate" },
  { id: "prepare-kit", label: "Prepare Kit" },
  { id: "handover", label: "Hand Over Kit to Client" },
  { id: "notice-to-client", label: "Notice to Client" },
  {
    id: "inform-client",
    label: "Inform Client",
  },
];

const STATUS_ID_TO_SLUG: Record<string, string> = {
  received: "legal-certificate-received",
  "prepare-kit": "prepare-kit",
  handover: "hand-over-kit-to-client",
  "notice-to-client": "notice-to-client",
  "inform-client": "inform-client-about-legal-certificate",
};

const STATUS_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const FORM_MAP: Record<string, string | undefined> = {
  preliminary: LEGAL_CERTIFICATE_PRELIMINARY_FORM_ID,
  "application-details": LEGAL_CERTIFICATE_APPLICATION_DETAILS_FORM_ID,
  invoice: LEGAL_CERTIFICATE_INVOICE_TO_CLIENT_FORM_ID,
  payment: LEGAL_CERTIFICATE_PAYMENT_FROM_CLIENT_FORM_ID,
  "stamp-paper-details-to-vendor":
    LEGAL_CERTIFICATE_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID,
  "stamp-paper-from-vendor":
    LEGAL_CERTIFICATE_STAMP_PAPER_FROM_VENDOR_FORM_ID,
  "preparation-of-power-of-attorney":
    LEGAL_CERTIFICATE_PREPARATION_OF_POA_FORM_ID,
  "file-forms": LEGAL_CERTIFICATE_FILE_FORMS_FORM_ID,
  "inform-client-of-filing":
    LEGAL_CERTIFICATE_INFORM_CLIENT_OF_FILING_FORM_ID,
  received: LEGAL_CERTIFICATE_STATUS_OF_LEGAL_CERTIFICATE_FORM_ID,
  "prepare-kit": LEGAL_CERTIFICATE_PREPARE_KIT_FORM_ID,
  handover: LEGAL_CERTIFICATE_HANDOVER_FORM_ID,
  "notice-to-client": LEGAL_CERTIFICATE_NOTICE_TO_CLIENT_FORM_ID,
  "inform-client": LEGAL_CERTIFICATE_INFORM_CLIENT_FORM_ID,
};

export default function DashboardLegalCertificate() {
  const navigate = useNavigate();
  const { section, step, leadId } = useParams<{
    section?: string;
    step?: string;
    leadId?: string;
  }>();

  const [menuOpen, setMenuOpen] = useState(false);
  const [markNameForHeader, setMarkNameForHeader] = useState<string | null>(
    null
  );
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [stampPaperSent, setStampPaperSent] = useState(false);
  const [informClientFilingSent, setInformClientFilingSent] = useState(false);
  const [noticeToClientSent, setNoticeToClientSent] = useState(false);
  const [informClientStatusSent, setInformClientStatusSent] = useState(false);
  const [progressing, setProgressing] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setMarkNameForHeader(null);
      return;
    }
    let cancelled = false;
    getLegalCertificateDiscussionDetails(leadId)
      .then((details) => {
        if (!cancelled)
          setMarkNameForHeader(details.preliminary.mark ?? null);
      })
      .catch(() => {
        if (!cancelled) setMarkNameForHeader(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeTab: Section =
    section === "filing"
      ? "filing"
      : section === "status"
        ? "status"
        : "discussion";

  const activeMenuItem =
    activeTab === "discussion" && step
      ? (DISCUSSION_SLUG_TO_ID[step] ?? "preliminary")
      : activeTab === "filing" && step
        ? (FILING_SLUG_TO_ID[step] ?? "stamp-paper-details-to-vendor")
        : activeTab === "status" && step
          ? (STATUS_SLUG_TO_ID[step] ?? "received")
          : activeTab === "filing"
            ? "stamp-paper-details-to-vendor"
            : activeTab === "status"
              ? "received"
              : "preliminary";

  useEffect(() => {
    if (!section || !step) {
      navigate(
        "/dashboard/legal-certificate/new/discussion/preliminary-discussion-with-the-client",
        { replace: true }
      );
    }
  }, [section, step, navigate]);

  const navigateToStep = (
    tab: Section,
    itemId: string,
    currentLeadId?: string | null
  ) => {
    const slug =
      tab === "discussion"
        ? DISCUSSION_ID_TO_SLUG[itemId]
        : tab === "filing"
          ? FILING_ID_TO_SLUG[itemId]
          : STATUS_ID_TO_SLUG[itemId];
    if (!slug) return;
    const base = `/dashboard/legal-certificate/new/${tab}/${slug}`;
    const path = currentLeadId ? `${base}/${currentLeadId}` : base;
    navigate(path);
    setMenuOpen(false);
  };

  const handleTabChange = (tab: Section) => {
    const firstStep =
      tab === "filing"
        ? "stamp-paper-details-to-vendor"
        : tab === "status"
          ? "received"
          : "preliminary";
    const slug =
      tab === "filing"
        ? FILING_ID_TO_SLUG[firstStep]
        : tab === "status"
          ? STATUS_ID_TO_SLUG[firstStep]
          : DISCUSSION_ID_TO_SLUG[firstStep];
    const path = leadId
      ? `/dashboard/legal-certificate/new/${tab}/${slug}/${leadId}`
      : `/dashboard/legal-certificate/new/${tab}/${slug}`;
    navigate(path);
  };

  const leftPanelItems =
    activeTab === "filing"
      ? FILING_ITEMS
      : activeTab === "status"
        ? STATUS_ITEMS
        : DISCUSSION_ITEMS;

  const activeLeftId = activeMenuItem;
  const activeIdx = leftPanelItems.findIndex((m) => m.id === activeLeftId);
  const canGoBack = activeIdx > 0;

  const submitForm = FORM_MAP[activeMenuItem] ?? null;
  const isInvoiceStep =
    activeTab === "discussion" && activeMenuItem === "invoice";
  const isStampPaperStep =
    activeTab === "filing" &&
    activeMenuItem === "stamp-paper-details-to-vendor";
  const isInformClientFilingStep =
    activeTab === "filing" && activeMenuItem === "inform-client-of-filing";
  const isNoticeToClientStep =
    activeTab === "status" && activeMenuItem === "notice-to-client";
  const isInformClientStatusStep =
    activeTab === "status" && activeMenuItem === "inform-client";
  const primaryActionLabel =
    (isInvoiceStep && !invoiceSent) ||
    (isStampPaperStep && !stampPaperSent) ||
    (isInformClientFilingStep && !informClientFilingSent) ||
    (isNoticeToClientStep && !noticeToClientSent) ||
    (isInformClientStatusStep && !informClientStatusSent)
      ? "Send & Continue"
      : "Save & Continue";

  // Progress (skip) button: shown on the Payment step.
  const progressStep =
    activeTab === "discussion" && activeMenuItem === "payment"
      ? { step: LegalCertificateWorkflowStep.LC_S1_04_PaymentFromClient }
      : null;

  const handleProgress = async () => {
    if (!progressStep || !leadId || progressing) return;
    setProgressing(true);
    try {
      await skipLegalCertificateStep(leadId, progressStep.step);
      toast.success("Step marked as skipped.");
      navigateToStep("filing", "stamp-paper-details-to-vendor", leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to skip step");
    } finally {
      setProgressing(false);
    }
  };

  return (
    <DashboardLayout
      headerTitle="Certificate"
      headerLeadNo={markNameForHeader ?? undefined}
      hideHeaderOnMobile
    >
      <div className="flex-1 p-2 min-h-0 flex flex-col min-w-0">
        <div className="bg-white rounded-[8px] h-full flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className="flex items-center gap-0 border-b border-[#EEEEEE] pl-[10px] pt-[6px] flex-shrink-0">
            {(["discussion", "filing", "status"] as Section[]).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-2.5 py-2.5 text-[14px] leading-5 -mb-px border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? "font-semibold text-[#222] border-[#222]"
                    : "font-normal text-[#222] border-transparent"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3 h-[65px] px-4 border-b border-[#E5E7EB] flex-shrink-0">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="flex items-center justify-center rounded-[10px] w-9 h-9 min-w-[36px] min-h-[36px] hover:bg-[#F7F7F7]"
            >
              <HiOutlineMenu
                className="w-5 h-5 text-[#222]"
                strokeWidth={1.5}
              />
            </button>
            <div className="flex-1 min-w-0 font-medium text-[14px] leading-5 text-[#242424] truncate">
              Legal Certificate
              {markNameForHeader ? ` | ${markNameForHeader}` : ""}
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden relative">
            {/* Mobile overlay */}
            {menuOpen && (
              <div
                className="absolute inset-0 bg-black/20 z-40 md:hidden"
                onClick={() => setMenuOpen(false)}
              />
            )}

            {/* Left panel */}
            <div
              className={`w-[279px] border-r border-[#EEEEEE] bg-white p-2.5 overflow-y-auto flex-shrink-0 ${
                menuOpen
                  ? "absolute left-0 top-0 bottom-0 z-50 md:relative"
                  : "hidden md:block"
              }`}
            >
              <div className="flex flex-col gap-2">
                {leftPanelItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() =>
                      navigateToStep(activeTab, item.id, leadId ?? null)
                    }
                    className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs ${
                      activeLeftId === item.id
                        ? "bg-[#222222] text-white font-medium"
                        : "bg-[#F7F7F7] text-[#222222] font-normal"
                    } hover:bg-opacity-90 transition-colors`}
                  >
                    <span className="text-left line-clamp-1">{item.label}</span>
                    <HiOutlineChevronRight
                      className="w-4 h-4 flex-shrink-0 ml-1"
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex-1 p-5 md:p-6 overflow-y-auto">
                {activeTab === "discussion" &&
                activeMenuItem === "preliminary" ? (
                  <LegalCertificatePreliminaryDiscussionView
                    leadId={leadId ?? undefined}
                    onSuccess={(newLeadId) => {
                      navigateToStep(
                        "discussion",
                        "application-details",
                        newLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "application-details" ? (
                  <LegalCertificateApplicationDetailsFromClientView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "discussion",
                        "invoice",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "invoice" ? (
                  <LegalCertificateInvoiceToClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInvoiceSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("discussion", "payment", currentLeadId);
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "payment" ? (
                  <LegalCertificatePaymentFromClientView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "stamp-paper-details-to-vendor",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "stamp-paper-details-to-vendor" ? (
                  <LegalCertificateStampPaperDetailsToVendorView
                    leadId={leadId ?? null}
                    onSentStatusChange={setStampPaperSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "stamp-paper-from-vendor",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "stamp-paper-from-vendor" ? (
                  <LegalCertificateStampPaperFromVendorView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "preparation-of-power-of-attorney",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "preparation-of-power-of-attorney" ? (
                  <LegalCertificatePreparationOfPowerOfAttorneyView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("filing", "file-forms", currentLeadId);
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "file-forms" ? (
                  <LegalCertificateFileFormsView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "inform-client-of-filing",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "inform-client-of-filing" ? (
                  <LegalCertificateInformClientOfFilingView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInformClientFilingSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("status", "received", currentLeadId);
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "received" ? (
                  <LegalCertificateStatusOfLegalCertificateView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("status", "prepare-kit", currentLeadId);
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "prepare-kit" ? (
                  <LegalCertificatePrepareKitView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep("status", "handover", currentLeadId);
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "handover" ? (
                  <LegalCertificateHandOverKitToClientView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "status",
                        "notice-to-client",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "notice-to-client" ? (
                  <LegalCertificateNoticeToClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setNoticeToClientSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "status",
                        "inform-client",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "inform-client" ? (
                  <LegalCertificateInformClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInformClientStatusSent}
                    onSuccess={() => {
                      navigate("/dashboard/legal-certificate");
                    }}
                  />
                ) : (
                  <InactiveStepView
                    title="Step coming soon"
                    body="This step has not been implemented yet. Please continue with the available steps."
                    buttonText="Back to Preliminary Discussion"
                    onButtonClick={() =>
                      navigateToStep(
                        "discussion",
                        "preliminary",
                        leadId ?? null
                      )
                    }
                  />
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-[#EEE] p-3 flex items-center justify-between gap-2 flex-shrink-0">
                <button
                  type="button"
                  disabled={!canGoBack}
                  onClick={() => {
                    if (!canGoBack) return;
                    navigateToStep(
                      activeTab,
                      leftPanelItems[activeIdx - 1].id,
                      leadId ?? null
                    );
                  }}
                  className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/legal-certificate")}
                    className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7]"
                  >
                    Close
                  </button>
                  {progressStep && (
                    <button
                      type="button"
                      onClick={handleProgress}
                      disabled={progressing}
                      className="px-4 py-2 h-9 text-[12px] font-semibold text-[#222] border border-[#222] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {progressing ? "Progressing…" : "Progress"}
                    </button>
                  )}
                  <button
                    type={submitForm ? "submit" : "button"}
                    form={submitForm ?? undefined}
                    disabled={!submitForm}
                    className="px-4 py-2 h-9 min-w-[128px] text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {primaryActionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
